/**
 * Shared helpers for the claim routing settings page: assignee field
 * builders (the progressive-disclosure "who gets it" block both dialogs
 * share), plain-English condition summaries for the rule rows, and the
 * trigger-state catalog lookup.
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import api from '@/admin/lib/api'
import { useOrg } from '@/admin/hooks/use-org'
import type { FormField } from '@/shared/lib/forms'
import {
  OP_LABELS,
  VALUELESS_OPS,
  predicateToBuilder,
} from '@/admin/components/condition-builder/types'
import type {
  RoutingAssignee,
  RoutingMeta,
  RoutingOption,
  RoutingStateOption,
} from '@/admin/hooks/use-routing-rules'

/** Claim-scoped ref lookup powering condition summaries + the builder. */
export type RefLookup = (ref: string) => { label: string } | undefined

// ── Assignee encode/decode (one select holds both roles and people) ──

export function encodeAssignee(assignee: RoutingAssignee | null): string {
  if (!assignee) return ''
  return `${assignee.kind}:${assignee.value}`
}

export function decodeAssignee(
  encoded: string,
): { kind: 'role' | 'person'; value: string } | null {
  if (!encoded) return null
  const separator = encoded.indexOf(':')
  if (separator < 1) return null
  const kind = encoded.slice(0, separator)
  const value = encoded.slice(separator + 1)
  if ((kind !== 'role' && kind !== 'person') || !value) return null
  return { kind, value }
}

// ── Org members (person picker options) ────────────────────────────

interface OrgMember {
  uuid: string
  user_email: string
  user_name: string
}

/**
 * ``undefined`` while the member list loads: the options feed the form's
 * fields, so a dialog that renders before they land remounts its form on
 * arrival and snaps shut any dropdown the user just opened.
 */
export function useMemberOptions(): RoutingOption[] | undefined {
  const { data: org } = useOrg()
  const orgUuid = org?.organisation?.uuid
  const { data: members, isPending } = useQuery<OrgMember[]>({
    queryKey: ['org-members', orgUuid],
    queryFn: () =>
      api.get(`/organisations/${orgUuid}/members/`).then((r) => {
        const data = r.data?.results ?? r.data
        return Array.isArray(data) ? data : []
      }),
    enabled: !!orgUuid,
    staleTime: 60_000,
  })
  return useMemo(
    () =>
      isPending
        ? undefined
        : (members ?? [])
            .filter((member) => member.user_email)
            .map((member) => ({
              value: member.user_email,
              label: member.user_name
                ? `${member.user_name} (${member.user_email})`
                : member.user_email,
            })),
    [members, isPending],
  )
}

// ── The shared "who gets it" form block (progressive disclosure) ─────

export function buildAssigneeFields(options: {
  roleOptions: RoutingOption[]
  personOptions: RoutingOption[]
  strategyOptions: RoutingOption[]
  /** Zone-owner role options; when empty the 'Zone owner' kind is hidden. */
  zoneRoleOptions?: RoutingOption[]
  initial: {
    kind: 'role' | 'person' | 'zone'
    role: string
    person: string
    zoneRole: string
    strategy: string
    prefer_creator: boolean
  }
}): FormField[] {
  const {
    roleOptions,
    personOptions,
    strategyOptions,
    zoneRoleOptions = [],
    initial,
  } = options
  const kindOptions = [
    { label: 'Role - shared between its members', value: 'role' },
    { label: 'Person - one named teammate', value: 'person' },
    ...(zoneRoleOptions.length
      ? [{ label: 'Zone owner - by the property location', value: 'zone' }]
      : []),
  ]
  return [
    {
      type: 'select',
      name: 'assignee_kind',
      label: 'Assign the claim to a',
      value: initial.kind,
      testId: 'routing-assignee-kind',
      options: kindOptions,
    },
    {
      type: 'select',
      name: 'role',
      label: 'Role',
      value: initial.role,
      testId: 'routing-role',
      options: roleOptions,
      visible: (ctx) => ctx.values.assignee_kind === 'role',
    },
    {
      type: 'select',
      name: 'person',
      label: 'Person',
      value: initial.person,
      testId: 'routing-person',
      options: personOptions,
      visible: (ctx) => ctx.values.assignee_kind === 'person',
    },
    {
      type: 'select',
      name: 'zone_role',
      label: 'Zone-owner role',
      value: initial.zoneRole,
      testId: 'routing-zone-role',
      options: zoneRoleOptions,
      visible: (ctx) => ctx.values.assignee_kind === 'zone',
    },
    {
      type: 'select',
      name: 'strategy',
      label: 'Share claims by',
      value: initial.strategy || 'least_busy',
      testId: 'routing-strategy',
      options: strategyOptions,
      visible: (ctx) => ctx.values.assignee_kind === 'role',
    },
    {
      type: 'checkbox',
      name: 'prefer_creator',
      label:
        'Assign a claim straight to the teammate who files it (they can still reassign)',
      value: initial.prefer_creator,
      visible: (ctx) => ctx.values.assignee_kind === 'role',
    },
  ]
}

/** Resolve the {kind, value} assignee from generated-form values. */
export function resolveAssigneeFromForm(
  values: Record<string, unknown>,
): { kind: 'role' | 'person' | 'zone'; value: string } {
  const kind = values.assignee_kind as 'role' | 'person' | 'zone'
  const raw =
    kind === 'role'
      ? values.role
      : kind === 'person'
        ? values.person
        : values.zone_role
  return { kind, value: String(raw ?? '') }
}

// ── Condition summaries (the "at a glance" line on each rule row) ────

function stringifyClauseValue(value: unknown): string {
  if (Array.isArray(value)) return value.join(', ')
  if (value === null || value === undefined || value === '') return ''
  return String(value)
}

/** Human summary of a rule's conditions, e.g. "approved amount at least 1,000". */
export function summariseConditions(
  conditions: unknown,
  byRef: RefLookup,
): string {
  if (!conditions || Object.keys(conditions).length === 0) return ''
  const clauses = predicateToBuilder(conditions).clauses.filter(
    (c) => c.ref && c.op,
  )
  if (!clauses.length) return ''
  return clauses
    .map((c) => {
      const label = byRef(c.ref)?.label ?? c.ref.split('.').pop() ?? c.ref
      const op = OP_LABELS[c.op] ?? c.op
      return VALUELESS_OPS.has(c.op)
        ? `${label} ${op}`
        : `${label} ${op} ${stringifyClauseValue(c.value)}`.trim()
    })
    .join(' and ')
}

// ── Trigger-state catalog (badge labels + colors, shared by rows) ────

export function useStateCatalog(meta: RoutingMeta | undefined) {
  return useMemo(() => {
    const byKey = new Map<string, RoutingStateOption>()
    for (const state of meta?.state_options ?? []) byKey.set(state.key, state)
    return byKey
  }, [meta])
}
